import { apiClient, createAuthHeaders } from "@/lib/api-client";

import type { SlideInfo } from "@/stores/slide-store";
import { useSlideStore } from "@/stores/slide-store";
import { savePendingJob, clearPendingJob } from "./export-job-tracker";

interface ExportOptions {
  title?: string;
  slides: SlideInfo[];
  projectId: string;
  getIdToken: () => Promise<string | null>;
}

/**
 * Export slides as PPTX via server-side generation.
 * Creates a BullMQ job, polls for completion, then downloads the file.
 */
export async function exportToPptx({
  title = "Presentation",
  slides,
  projectId,
  getIdToken,
}: ExportOptions): Promise<void> {
  const token = await getIdToken();
  if (!token) throw new Error("Not authenticated");
  const headers = createAuthHeaders(token);

  // 1. Start export job
  const { data: job } = await apiClient.post(
    "/api/slides/export",
    {
      format: "pptx",
      projectId,
      slideCount: slides.length,
      title,
    },
    { headers },
  );

  // Save to localStorage so it survives page reloads
  savePendingJob({
    jobId: job.jobId,
    format: "pptx",
    title,
    projectId,
    createdAt: Date.now(),
  });

  // 2. Poll for completion (updates progress in store)
  const result = await pollUntilComplete(job.jobId, headers);

  if (result.status === "failed") {
    clearPendingJob();
    throw new Error(result.error || "Export failed");
  }

  // 3. Download file
  const blob = await apiClient.get(
    `/api/slides/export/${job.jobId}/download`,
    {
      headers,
      responseType: "blob",
    },
  );

  clearPendingJob();

  downloadBlob(
    blob.data,
    result.result?.fileName || `${sanitizeFilename(title)}.pptx`,
  );
}

async function pollUntilComplete(
  jobId: string,
  headers: Record<string, string>,
  maxAttempts = 120,
  intervalMs = 1000,
): Promise<{
  status: string;
  error?: string;
  result?: { fileName: string; fileSize: number };
}> {
  const store = useSlideStore.getState();

  for (let i = 0; i < maxAttempts; i++) {
    const { data } = await apiClient.get(
      `/api/slides/export/${jobId}/status`,
      { headers },
    );

    if (typeof data.progress === "number") {
      store.setExportProgress(data.progress);
    }

    if (data.status === "completed" || data.status === "failed") {
      return data;
    }

    await new Promise((r) => setTimeout(r, intervalMs));
  }

  throw new Error("Export timed out");
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9-_ ]/g, "").trim() || "presentation";
}
